'use strict';
import * as _ from 'lodash';
import moment from 'moment';
import {module} from '../app';
import {displayNone} from "../constants/displayStyleConstants";
import * as constantStrings from "../constants/constantStrings";
import { FollowUpHelpers } from "../helpers/followUpHelpers";

module
  .factory('FollowUpDetailDialogModel', [

    '$q', 'Patient', 'modalService', 'FollowUpsDomain', 'Worklist', 'ReferencePhysician', 'Study',
    '$ionicLoading', 'tpConfig', 'signalrService', 'ionicDatePicker', 'datePickerService', 'ageHelper',

    function ($q, Patient, modalService, FollowUpsDomain, Worklist, ReferencePhysician, Study,
              $ionicLoading, tpConfig, signalrService, ionicDatePicker, datePickerService, ageHelper) {

      function FollowUpDetailDialogModel(patientSsn, sdate, customFollowUpDetails, defaultRefPhy, followUpId, canEdit = true, checkIfStillOwningAndNotOffline) {

        let self = this;

        self.getAllRefPhyPromise = null;
        self.checkIfStillOwningAndNotOffline = checkIfStillOwningAndNotOffline;
        self.requiredFields = ['Location', 'StudyDate', 'RecallDate', 'Assessment', 'Recommendation', 'Side', 'RefNum'];

        const refPhyFieldTitle = 'Referring Physician';
        const refPhyFieldName = 'RefNum';
        let networkUnavailableMessage = 'Network unavailable. Please check your network settings.';
        let clientSideDateFormat = 'MM/DD/YYYY';
        self.canEdit = canEdit;
        self.followUpId = followUpId;
        self.sdate = sdate;
        self.examDetails = customFollowUpDetails;
        self.recommendations = [];
        self.assessments = [];
        self.refPhys = [defaultRefPhy];
        self.queueRequests = [];
        self.patientSsn = patientSsn;
        self.patientInfo = null;
        self.associatedExams = [];
        self.modalityOptions = [
          {
            Code: 'M',
            Description: 'Mammogram'
          },
          {
            Code: 'U',
            Description: 'Ultrasound'
          },
          {
            Code: 'R',
            Description: 'MRI'
          },
          {
            Code: 'B',
            Description: 'Biopsy'
          }
        ];
        self.sideOptions = [
          {
            Code: 'L',
            Description: 'Left'
          },
          {
            Code: 'R',
            Description: 'Right'
          },
          {
            Code: 'B',
            Description: 'Bilateral'
          }
        ];
        self.isLoadingMore = false;
        self.pageIndex = 0;
        self.stopInfiniteLoad = true;
        self.query = undefined;
        self.loadedRefPhies = [];
        self.templateData = {
          showLoadMore: true
        };

        self.modalInstance = null;

        if (defaultRefPhy && defaultRefPhy.RefNum) {
          self.refPhyPlaceholder = composeRefPhyDisplayName(defaultRefPhy);
        }

        self.data = {
          Location: '',
          Ssn: patientSsn,
          StudyDate: null,
          RecallDate: null,
          Assessment: '',
          Recommendation: '',
          Modality: '',
          Side: '',
          NextLetterType: '',
          Notes: '',
          RefNum: defaultRefPhy && defaultRefPhy.RefNum,
          AssociatedExam: ''
        };
        self.canceledLoadRefPhys = false;
        self.isInitialized = false;
        self.originalData = _.cloneDeep(self.data);

        self.displayPatientAge = '';
        self.displayReferringPhysician = '-';
        self.displayFullAddress = '-';

        self._updateDisplayProperties = function() {
          self.displayPatientAge = self._computePatientAge();
          self.displayReferringPhysician = self._computeReferringPhysicianDisplay();
          self.displayFullAddress = self._computeFullAddress();
        };

        self._computePatientAge = function () {
          if (self.patientInfo && self.patientInfo.DOB) {
            let dateOfBirth = new Date(self.patientInfo.DOB);
            return ageHelper.getAgeFromBirthDate(dateOfBirth);
          }
          return '';
        };

        self._computeReferringPhysicianDisplay = function () {
          if (self.patientInfo && self.patientInfo.RefPhyLastName && self.patientInfo.RefPhyFirstName) {
            return `${self.patientInfo.RefPhyLastName}, ${self.patientInfo.RefPhyFirstName}`;
          }
          
          if (self.patientInfo && self.patientInfo.ReferringPhysicians && Array.isArray(self.patientInfo.ReferringPhysicians)) {
            const refPhy = self.patientInfo.ReferringPhysicians.find(rp => rp !== null && rp !== undefined);
            if (refPhy && refPhy.LastName && refPhy.FirstName) {
              const lastName = refPhy.LastName.trim();
              const firstName = refPhy.FirstName.trim();
              return `${lastName}, ${firstName}`;
            }
          }
          
          return '-';
        };

        self._computeFullAddress = function () {
          if (!self.patientInfo) return '-';
          
          let addressParts = [];
          
          const street = self.patientInfo.Address || self.patientInfo.Street;
          if (street && street.trim()) addressParts.push(street.trim());
          
          if (self.patientInfo.Street2 && self.patientInfo.Street2.trim()) {
            addressParts.push(self.patientInfo.Street2.trim());
          }
          
          if (self.patientInfo.City && self.patientInfo.City.trim()) {
            addressParts.push(self.patientInfo.City.trim());
          }
          
          if (self.patientInfo.State && self.patientInfo.State.trim()) {
            addressParts.push(self.patientInfo.State.trim());
          }
          
          if (self.patientInfo.Zip && self.patientInfo.Zip.trim()) {
            addressParts.push(self.patientInfo.Zip.trim());
          }
          
          if (self.patientInfo.Country && self.patientInfo.Country.trim() && self.patientInfo.Country.trim() !== 'USA') {
            addressParts.push(self.patientInfo.Country.trim());
          }
          
          return addressParts.length > 0 ? addressParts.join(', ') : '-';
        };

        self.getPatientAge = function () {
          return self.displayPatientAge;
        };

        self.getReferringPhysicianDisplay = function () {
          return self.displayReferringPhysician;
        };

        self.getFullAddress = function () {
          return self.displayFullAddress;
        };

        self._getPatientInfo = async function () {
          try {
            const response = await Patient.get(self.patientSsn);
            self.patientInfo = response.data;
            self._updateDisplayProperties();
            return response;
          } catch (error) {
            if (error.status === -1) {
              self.needRefresh = true;
            }
            throw error;
          }
        };

        self.getRecommendations = function () {
          if (!self.data.Assessment) {
            return [];
          }

          let recommendations = _.filter(self.recommendations, function (recommendation) {
            return _.includes(recommendation.SpecialCondition, self.data.Assessment.trim());
          });

          return recommendations;
        };

        self.getRefPhyAutocomplete = function (query) {

          if (self.query === query) {
            self.pageIndex++;
            self.isLoadingMore = true;
          } else {
            self.isLoadingMore = false;
          }

          if (!self.query) {
            self.query = '';
          }

          self.query = query;

          if (self.getAllRefPhyPromise && self.getAllRefPhyPromise.canceler) {
            self.canceledLoadRefPhys = true;
            self.getAllRefPhyPromise.canceler.resolve();
          }

          var usedRefPhyKeys = [];
          if (self.data.RefNum) {
            usedRefPhyKeys.push(Array.isArray(self.data.RefNum) ? self.data.RefNum[0] : self.data.RefNum);
          }

          self.getAllRefPhyPromise = ReferencePhysician.loadForAutocomplete(query, self.pageIndex || 0, usedRefPhyKeys, self.isInitialized);

          return self.getAllRefPhyPromise
            .then(function (response) {
              self.canceledLoadRefPhys = false;
              self.stopInfiniteLoad = response.data.length === 0;
              self.templateData.showLoadMore = !self.stopInfiniteLoad;

              const newMapArray = response.data.map(function (record) {
                record.DisplayName = composeRefPhyDisplayName(record);
                return record;
              });
              if (self.isLoadingMore) {
                self.loadedRefPhies = self.loadedRefPhies.concat(newMapArray);
              } else {
                self.loadedRefPhies = newMapArray;
              }

              return {
                List: self.loadedRefPhies,
                IsError: false,
                ErrorMessage: ''
              }
            }, function (error) {
              if (self.canceledLoadRefPhys) {
                return;
              }
              self.stopInfiniteLoad = true;

              let message = error.statusText;
              if (error.status == -1) {
                message = networkUnavailableMessage;
              }

              return { List: [], IsError: true, ErrorMessage: message };
            }
            );
        };

        self.getRefPhyByKey = async function (selectedKey) {

          if (!selectedKey || !selectedKey.length) {
            return;
          }

          let filteredRefPhys = self.getRefPhyFromCache(selectedKey);
          if (filteredRefPhys && filteredRefPhys.length) {
            return filteredRefPhys;
          }

          filteredRefPhys = self.getRefPhyFromRecentAutocomplete(selectedKey);
          if (filteredRefPhys && filteredRefPhys.length) {
            return filteredRefPhys;
          }

          let refPhy = await ReferencePhysician.get(Array.isArray(selectedKey) ? selectedKey[0] : selectedKey);
          if (refPhy && !self.refPhys.some(ref => ref.RefNum.trim() === refPhy.RefNum.trim())) {
            refPhy.DisplayName = composeRefPhyDisplayName(refPhy);
            self.refPhys.push(refPhy);
          }
          return [refPhy];
        };

        self.getRefPhyFromCache = function (selectedKey) {
          return self.getRefPhyFromArray(selectedKey, self.refPhys);
        }

        self.getRefPhyFromRecentAutocomplete = function (selectedKey) {
          let items = self.getRefPhyFromArray(selectedKey, self.loadedRefPhies);
          items.forEach(item => {
            if (item && !self.refPhys.some(ref => ref.RefNum.trim() === item.RefNum.trim())) {
              self.refPhys.push(item);
            }
          });
          return items;
        };

        self.getRefPhyFromArray = function (selectedKey, array) {
          if (!array || array.length == 0) {
            return [];
          }
          return array.filter(function (element) {
            if (Array.isArray(selectedKey)) {
              return element.RefNum === selectedKey[0];
            } else {
              return (element.RefNum && element.RefNum.trim()) === (selectedKey && selectedKey.trim());
            }
          });
        };

        self.refPhySelected = function (callback) {
          self.resetAutocomplete();
        };

        self.resetAutocomplete = function () {
          self.query = undefined;
          self.pageIndex = 0;
          self.isLoadingMore = false;
          self.onRefPhyAutocompleteModalClosed();
        };

        self.save = async function () {
          let missingFields = [];

          _.each(self.requiredFields, function (field) {
            if (!self.data[field] || (self.data[field] && self.data[field].length == 0)) {
              if(field === refPhyFieldName){
                missingFields.push(refPhyFieldTitle);
              }else{
                missingFields.push(field.split(/(?=[A-Z])/).join(' '));
              }
            }
          });

          if (missingFields.length) {
            let msg = 'The following fields are required: ' + missingFields.join(', ') + '. Please correct your data.';
            modalService.showAlert(msg);
            return;
          }

          if (self.data.StudyDate > new Date()) {
            let msg = 'Study date must be in the past.';
            modalService.showAlert(msg);
            return;
          }

          $ionicLoading.show({template: constantStrings.processingLoaderMessage});
          const isOwnershipLost = !await self.checkIfStillOwningAndNotOffline();
          if(isOwnershipLost){
            $ionicLoading.hide();
            self.modalInstance.remove();
            return;
          }

          self.executeSaveData();
        };

        self._getClonedData = function () {
          let clonedData = _.cloneDeep(self.data);
          clonedData.StudyDate = moment(clonedData.StudyDate).format(clientSideDateFormat);
          clonedData.RecallDate = moment(clonedData.RecallDate).format(clientSideDateFormat);
          clonedData.RefNum = angular.isArray(clonedData.RefNum) ? clonedData.RefNum[0] : clonedData.RefNum;
          
          if (clonedData.AssociatedExam) {
            const selectedExam = FollowUpHelpers.findExamBySDate(self.associatedExams, clonedData.AssociatedExam);
            if (selectedExam) {
              clonedData.SDate = selectedExam.SDate;
              clonedData.Accession = selectedExam.Accession;
            } else {
              clonedData.SDate = self.sdate;
              clonedData.Accession = self.examDetails && self.examDetails.Accession;
            }
          } else {
            clonedData.SDate = self.sdate;
            clonedData.Accession = self.examDetails && self.examDetails.Accession;
          }
          
          return clonedData;
        }

        self.executeSaveData = async function () {
          const clonedData = self._getClonedData();
          const saveOperation = self.followUpId 
            ? FollowUpsDomain.updateFollowUps(self.followUpId, clonedData)
            : FollowUpsDomain.createFollowUps(clonedData);

          $ionicLoading.show({
            template: 'Saving follow-up. Please wait...'
          });

          try {
            await saveOperation;
            $ionicLoading.hide();
            modalService.showAlert('Follow-up saved successfully.', () => {
              self.modalInstance.remove();
            });
          } catch (error) {
            $ionicLoading.hide();
            if (error.status === -1) {
              self.needRetrySave = true;
              $ionicLoading.show({
                template: 'Network problem. Retrying...'
              });
            }
            console.error('Error saving follow-up:', error);
          }
        };

        self._hasChanged = function () {
          let clonedData = _.cloneDeep(self.data);
          if (Array.isArray(clonedData.RefNum)) {
            clonedData.RefNum = clonedData.RefNum[0];
          }
          ;
          return !_.isEqual(clonedData, self.originalData);
        }

        self.init = async function (modal, scope) {
          self.modalInstance = modal;
          self._scope = scope;

          self._scope.$on(tpConfig.events.portalNetworkAvailabilityChanged, self.onNetworkAvailabilityChanged);

          try {
            await self.initializeData();
            if (self.followUpId) {
              await self._getNextLetterType(self.followUpId);
            }
          } catch (error) {
            console.error('Error initializing follow-up dialog:', error);
          }
        };

        self.onNetworkAvailabilityChanged = async function (scope, data) {
          if (data.isOnline) {
            if (self.needRefresh) {
              self.needRefresh = false;
              try {
                await self.initializeData();
                if (self.followUpId) {
                  await self._getNextLetterType(self.followUpId);
                }
              } catch (error) {
                console.error('Error refreshing data:', error);
              }
            }
            if (self.needRetrySave) {
              self.needRetrySave = false;
              await self.executeSaveData();
            }
          }
        };

        self._getLocations = async function () {
          try {
            const response = await Worklist.getLocations();
            self.locations = response.data;
            return response;
          } catch (error) {
            if (error.status === -1) {
              self.needRefresh = true;
            }
            throw error;
          }
        };

        self._getRecommendations = async function () {
          try {
            const response = await FollowUpsDomain.getRecommendations();
            self.recommendations = response;
            return response;
          } catch (error) {
            if (error.status === -1) {
              self.needRefresh = true;
            }
            throw error;
          }
        };

        self._getNextLetterType = async function (followUpId) {
          try {
            const response = await FollowUpsDomain.getNextLetterType(self.patientSsn, self.sdate, followUpId);
            self.data.NextLetterType = response;
            return response;
          } catch (error) {
            if (error.status === -1) {
              self.needRefresh = true;
            }
            throw error;
          }
        };

        self._getAssessments = async function () {
          try {
            const response = await FollowUpsDomain.getAssessments();
            self.assessments = _.orderBy(response, e => e.Description, ['asc']);
            return response;
          } catch (error) {
            if (error.status === -1) {
              self.needRefresh = true;
            }
            throw error;
          }
        };

        self._getFollowUpDetail = async function (followUpId) {
          try {
            const response = await FollowUpsDomain.getFollowUp(followUpId);
            response.StudyDate = new Date(moment(response.StudyDate).format(clientSideDateFormat));
            response.RecallDate = new Date(moment(response.RecallDate).format(clientSideDateFormat));
            self.data = response;
            self.originalData = _.cloneDeep(self.data);
            return response;
          } catch (error) {
            if (error.status === -1) {
              self.needRefresh = true;
            }
            throw error;
          }
        };

        self._setAssociatedExamFromSDate = function () {
          if (!self.data || !self.data.SDate || !self.associatedExams || self.associatedExams.length === 0) {
            return;
          }

          const matchingExam = FollowUpHelpers.findExamBySDate(self.associatedExams, self.data.SDate);
          if (matchingExam) {
            self.data.AssociatedExam = matchingExam.Key;
          }
        };

        self.initializeData = async function () {
          if (!signalrService.isConnected) {
            self.needRefresh = true;
            modalService.showAlert(networkUnavailableMessage);
            return;
          }

          const promises = [
            self._getLocations(),
            self._getRecommendations(),
            self._getAssessments(),
            self._getPatientInfo(),
            self._getAssociatedExams()
          ];

          if (self.followUpId) {
            promises.push(self._getFollowUpDetail(self.followUpId));
          }

          $ionicLoading.show('Loading...');
          
          try {
            await Promise.all(promises);
            
            if (self.followUpId && !self.needRefresh) {
              self._setAssociatedExamFromSDate();
            }
            
            if (!self.needRefresh) {
              self.autoOpenAssociatedExamDropdown();
            }
          } catch (error) {
            console.error('Error initializing follow-up data:', error);
          } finally {
            $ionicLoading.hide();
            if (self.needRefresh) {
              modalService.showAlert(networkUnavailableMessage);
            }
          }
        };

        self.closeWindow = function () {
          if (self._hasChanged()) {
            modalService.showConfirmation(null, 'You have unsaved changes. Are you sure you want to cancel?')
              .then(function (confirmed) {
                if (confirmed) {
                  self.close();
                }
              });
            return;
          }
          self.close();
        };

        self.close = function () {
          self.modalInstance.remove();
        };

        self.changeDate = function (field) {
          const changeDateModel = {
            obj: self.data,
            id: field,
            minDate: field === 'RecallDate' 
              ? new Date(Date.now() + 24 * 60 * 60 * 1000) // Tomorrow
              : new Date(1900, 0, 1), // January 1, 1900
            maxDate: field === 'StudyDate' ? new Date() : null
          };
          
          if (field === 'StudyDate') {
            const originalCallback = changeDateModel.callback;
            changeDateModel.callback = function() {
              if (originalCallback) {
                originalCallback();
              }
              
              if (self.data.Recommendation && !self.followUpId) {
                self.onRecommendationSelected(self.data.Recommendation);
              }
            };
          }
          
          datePickerService.changeDate(changeDateModel);
        };

        self.onRefPhyAutocompleteModalClosed = ($event) => {
          // Fixed issue when Modality/Side dropdowns are being opened on selecting ref phy in followUpEditRefPhyDialog
          // Android: preventing followUpDetail modal clicks when followUpEditRefPhyDialog is opened
          // timeout needed to make sure that modal & keyboard is closed on android tablet
          setTimeout(() => {
            self.refPhyPlaceholder = '';
            self.canEdit = true;
          }, 50);
        };

        self.onRefPhyAutocompleteInputClick = ($event) => {
          // Fixed issue when Modality/Side dropdowns are being opened on selecting ref phy in followUpEditRefPhyDialog
          // Android: preventing followUpDetail modal clicks when followUpEditRefPhyDialog is opened
          self.canEdit = false;
        };

        document.onRefPhyAutocompleteTouchEnd = (event) => {
          // Fixed issue when Modality/Side dropdowns are being opened on selecting ref phy in followUpEditRefPhyDialog
          // touchend event can have different position than touchstart event. In this case followUpEditRefPhyDialog
          // will not be opened. This code is making sure that it was opened on touchend event
          setTimeout(() => {
            const followUpEditRefPhyDialogElement = document.getElementById('follow-up-edit-ref-phy-dialog');
            const shouldDisableEditing = followUpEditRefPhyDialogElement && followUpEditRefPhyDialogElement.style.display !== displayNone;
            if (shouldDisableEditing) {
              self.canEdit = false;
            }
          }, 0);
        };

        self._getAssociatedExams = async function () {
          try {
            const response = await Study.loadStudies(self.patientSsn);
            const transformedExams = FollowUpHelpers.transformStudiesToExams(response.data);
            self.associatedExams = FollowUpHelpers.sortExamsByDate(transformedExams);
            return response;
          } catch (error) {
            self.associatedExams = [];
            if (error.status === -1) {
              self.needRefresh = true;
            }
            throw error;
          }
        };

        self.onAssociatedExamSelected = function(selectedSDate) {
          if (!selectedSDate) return;
          
          const selectedExam = FollowUpHelpers.findExamBySDate(self.associatedExams, selectedSDate);
          if (selectedExam) {
            self.data.StudyDate = new Date(selectedExam.StudyDate);
            
            if (self.data.Recommendation && !self.followUpId) {
              self.onRecommendationSelected(self.data.Recommendation);
            }
          }
        };

        self.onRecommendationSelected = function(selectedRecommendationCode) {
          if (!selectedRecommendationCode || !self.data.StudyDate) return;
          
          if (self.followUpId) return;
          
          const selectedRecommendation = FollowUpHelpers.findRecommendationByCode(self.recommendations, selectedRecommendationCode);
          if (selectedRecommendation && selectedRecommendation.SpecialCondition) {
            const recallDate = FollowUpHelpers.calculateRecallDate(self.data.StudyDate, selectedRecommendation.SpecialCondition);
            if (recallDate) {
              self.data.RecallDate = recallDate;
            }
          }
        };



        self.autoOpenAssociatedExamDropdown = function() {
          if (!self.followUpId && self.canEdit && self.associatedExams.length > 0) {
            requestAnimationFrame(() => {
              FollowUpHelpers.activateDropdown('.associated-exam-dropdown');
            });
          }
        };
      }

      function composeRefPhyDisplayName(item) {
        return `${item.LastName && item.LastName.trim()} ${item.FirstName && item.FirstName.trim()} (${item.RefNum.trim()})`;
      }

      return FollowUpDetailDialogModel;
    }
  ]);



// WEBPACK FOOTER //
// ./src/scripts/models/followUpDetail.js